import { type ResultOf, useSession } from '@horof/shared';
import { graphql } from '../gql';

/**
 * The documents on the phone (doc 54): look one up, or scan a paper page by
 * page. Editing, reordering and replies are on the desk (doc 53).
 */

export const documentsContext = {
  additionalTypenames: ['AssociationDocument', 'AssociationDocumentFile'],
};

export const PhoneDocumentsDocument = graphql(`
  query PhoneDocuments {
    associationDocuments(
      orderBy: [DOCUMENT_DATE_DESC, CREATED_AT_DESC]
      first: 300
    ) {
      nodes {
        id
        type
        title
        documentDate
        reference
        isCurrent
        replyDueOn
        repliedById
        counterparty {
          name
        }
      }
    }
  }
`);

export type PhoneDocumentRow = NonNullable<
  NonNullable<
    ResultOf<typeof PhoneDocumentsDocument>['associationDocuments']
  >['nodes'][number]
>;

export const PhoneDocumentDocument = graphql(`
  query PhoneDocument($id: UUID!) {
    associationDocument(id: $id) {
      id
      type
      title
      documentDate
      reference
      notes
      replyDueOn
      repliedById
      isCurrent
      counterparty {
        name
      }
      project {
        name
      }
      repliedBy {
        id
        title
        reference
      }
      replyTo: associationDocumentsByRepliedById {
        nodes {
          id
          title
          reference
        }
      }
      files: associationDocumentFilesByDocumentId(orderBy: [POSITION_ASC]) {
        nodes {
          id
          photo {
            id
            url
            contentType
          }
        }
      }
    }
  }
`);

export const PhoneCounterpartiesDocument = graphql(`
  query PhoneCounterparties {
    counterparties(orderBy: [NAME_ASC]) {
      nodes {
        id
        name
      }
    }
  }
`);

export const PhoneCreateDocumentDocument = graphql(`
  mutation PhoneCreateDocument($doc: AssociationDocumentInput!) {
    createAssociationDocument(input: { associationDocument: $doc }) {
      associationDocument {
        id
        reference
      }
    }
  }
`);

export const PhoneAddFileDocument = graphql(`
  mutation PhoneAddDocumentFile($file: AssociationDocumentFileInput!) {
    createAssociationDocumentFile(input: { associationDocumentFile: $file }) {
      associationDocumentFile {
        id
      }
    }
  }
`);

/** The bureau reads and writes documents; meter readers don't (arqam 52). */
export function useSeesDocuments(): boolean {
  const { roles } = useSession();
  return roles.includes('committee') || roles.includes('treasurer');
}
